import React, { useEffect, useRef } from "react";
import { Bell, ChevronRight } from "lucide-react";
import SwipeToRead from "@/components/notifications/SwipeToRead";
import { Card } from "@/components/ui/card";
import { fmtDate } from "@/components/PageHeader";
import { shortenNos } from "@/lib/caseNo";

// Digest shortcut chips: each opens its own review page (the row itself opens n.link).
export function QuickLinks({ n, onOpen }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`notif-quick-links-${n.id}`}>
      {n.links.map((l, i) => (
        <button key={l.to + l.label} type="button" onClick={(e) => { e.stopPropagation(); onOpen(n, l.to); }}
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${l.label === "Review all" ? "bg-slate-900 text-white hover:bg-slate-700" : "border border-sky-200 bg-white text-sky-700 hover:bg-sky-50"}`}
          data-testid={`notif-quick-link-${n.id}-${i}`}>
          {l.label} <ChevronRight className="h-3 w-3" />
        </button>
      ))}
    </div>
  );
}

// Wraps case-insensitive matches of `word` in a highlight (archive search).
export function Highlight({ text, word }) {
  if (!word || !text) return text || null;
  const parts = text.split(new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) => (i % 2 ? <mark key={i} className="rounded bg-amber-200/80 px-0.5 text-slate-900" data-testid="notif-search-hit">{p}</mark> : p));
}

// One notification on the Notifications page (Active or Archive). Unread rows: swipe/drag left to mark read.
export default function NotifRow({ n, tag, highlight, onOpen, onRead, hit }) {
  const ref = useRef(null);
  useEffect(() => { if (highlight) ref.current?.scrollIntoView({ block: "center", behavior: "smooth" }); }, [highlight]);
  return (
    <SwipeToRead enabled={!n.read} onSwiped={() => onRead(n.id)} testId={`notif-swipe-${n.id}`}>
    <Card ref={ref} data-testid={`notif-row-${n.id}`} onClick={() => onOpen(n)} role={n.link ? "button" : undefined}
      className={`flex items-start gap-4 border-slate-200 p-4 transition-[background-color,border-color,box-shadow] ${n.link ? "cursor-pointer hover:border-sky-300 hover:bg-sky-50/40" : ""} ${n.read ? "" : "border-sky-200 bg-sky-50/60"} ${highlight ? "ring-2 ring-sky-400 ring-offset-2" : ""}`}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${n.read ? "bg-slate-100 text-slate-400" : "bg-sky-100 text-sky-600"}`}>
        <Bell className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-slate-800"><Highlight text={n.title} word={hit} /></p>
          {tag && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tag.cls}`} data-testid={`notif-tag-${n.id}`}>{tag.label}</span>}
        </div>
        <p className="text-sm text-slate-500"><Highlight text={shortenNos(n.message)} word={hit} /></p>
        {n.links?.length > 0 && <QuickLinks n={n} onOpen={onOpen} />}
        <p className="mt-1 text-xs text-slate-400">{fmtDate(n.created_at)}</p>
      </div>
      {n.link && <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" data-testid={`notif-link-${n.id}`} />}
    </Card>
    </SwipeToRead>
  );
}
